import React, { useEffect } from 'react'
import styled from 'styled-components'
import { FormControl, RadioGroup, FormControlLabel, Radio } from '@mui/material'

import LinkButton from '../../components/LinkButton'
import SlideContainer from '../../components/Layout/SlideContainer'

import i18n from '../../i18n'
import breakpoints from '../../constants/breakpoints'
import { Answer, Slide } from '../../types'
import { getLocalStorage, getNextSkipRoute, setLocalStorage } from '../../utils'
import Readspeaker from '../../components/Readspeaker'
import { formControlLabel, radioGroup, formControl } from './styles'

interface Props {
  routeId: string
  basePath: string
  nextLink: string
  button: string
  subtitle: string
  title: string
  customAnswers?: Answer[]
  routeIndex?: string
  routes: Slide[]
  onSkipRoute: (path: string) => void
}

const LikertScale: React.FC<Props> = ({
  routeId,
  basePath,
  button,
  nextLink,
  subtitle,
  title,
  customAnswers,
  routeIndex,
  routes,
  onSkipRoute
}) => {
  const answersInStorage = getLocalStorage('answers')
  const foundAnswer =
    answersInStorage && answersInStorage.find(answer => answer.routeId === routeId)
  const [value, setValue] = React.useState({
    routeId: routeId,
    answer: foundAnswer ? foundAnswer.answer[0] : '',
    isConditionalRoute:
      foundAnswer && foundAnswer.isConditionalRoute ? foundAnswer.isConditionalRoute : false
  })

  useEffect(() => {
    const nextRoute = routeIndex && getNextSkipRoute(routeIndex, routes)
    nextRoute && onSkipRoute(nextRoute.path)
  }, [])

  const handleChange = (answer: string, isConditionalRoute = false) => {
    setValue({
      routeId,
      answer: answer,
      isConditionalRoute
    })
  }

  const handleLocalStorage = () => {
    setLocalStorage(
      { routeId, answer: [value.answer], isConditionalRoute: value.isConditionalRoute },
      basePath
    )
  }

  return (
    <SlideContainer>
      <Content>
        <FixedWidth>
          <Subtitle>{subtitle}</Subtitle>
          <h1 id={title.replace(/[^A-Z0-9]+/gi, '_')}>{title}</h1>
          <Readspeaker />
        </FixedWidth>

        <FormControl component="fieldset" sx={[formControl]}>
          {customAnswers ? (
            <>
              <legend className="sr-only">{title}</legend>
              <RadioGroup
                value={value.answer}
                aria-labelledby={title.replace(/[^A-Z0-9]+/gi, '_')}
                name="customized-radios"
                sx={[radioGroup]}
              >
                {customAnswers.map((answer, index) => {
                  return (
                    <ButtonContainer checked={value.answer === answer.title} key={index}>
                      <FormControlLabel
                        onClick={() => {
                          const isConditionalRoute =
                            !!answer.conditionalRoute || !!answer.nextRouteId
                          handleChange(answer.title, isConditionalRoute)
                        }}
                        value={answer.title}
                        control={<Radio color="primary" />}
                        label={answer.title}
                        sx={[formControlLabel]}
                      />
                    </ButtonContainer>
                  )
                })}
              </RadioGroup>
            </>
          ) : (
            <>
              <legend className="sr-only">{title}</legend>
              <RadioGroup
                value={value.answer}
                aria-labelledby={title.replace(/[^A-Z0-9]+/gi, '_')}
                name="customized-radios"
                onChange={event => handleChange(event.target.value)}
                sx={[radioGroup]}
              >
                <ButtonContainer checked={value.answer === i18n.t('likertScale.disagree')}>
                  <FormControlLabel
                    value={i18n.t('likertScale.disagree')}
                    control={<Radio color="primary" />}
                    label={i18n.t('likertScale.disagree')}
                    sx={[formControlLabel]}
                  />
                </ButtonContainer>

                <ButtonContainer checked={value.answer === i18n.t('likertScale.reasonablyDisagre')}>
                  <FormControlLabel
                    value={i18n.t('likertScale.reasonablyDisagre')}
                    control={<Radio color="primary" />}
                    label={i18n.t('likertScale.reasonablyDisagre')}
                    sx={[formControlLabel]}
                  />
                </ButtonContainer>

                <ButtonContainer checked={value.answer === i18n.t('likertScale.doNotCare')}>
                  <FormControlLabel
                    value={i18n.t('likertScale.doNotCare')}
                    control={<Radio color="primary" />}
                    label={i18n.t('likertScale.doNotCare')}
                    sx={[formControlLabel]}
                  />
                </ButtonContainer>

                <ButtonContainer checked={value.answer === i18n.t('likertScale.reasonablyAgree')}>
                  <FormControlLabel
                    value={i18n.t('likertScale.reasonablyAgree')}
                    control={<Radio color="primary" />}
                    label={i18n.t('likertScale.reasonablyAgree')}
                    sx={[formControlLabel]}
                  />
                </ButtonContainer>

                <ButtonContainer checked={value.answer === i18n.t('likertScale.agree')}>
                  <FormControlLabel
                    value={i18n.t('likertScale.agree')}
                    control={<Radio color="primary" />}
                    label={i18n.t('likertScale.agree')}
                    sx={[formControlLabel]}
                  />
                </ButtonContainer>
              </RadioGroup>
            </>
          )}
        </FormControl>

        {nextLink && (
          <Next>
            <LinkButton onClick={handleLocalStorage} to={nextLink}>
              {button}
            </LinkButton>
          </Next>
        )}
      </Content>
    </SlideContainer>
  )
}

const FixedWidth = styled.div`
  max-width: 700px;
`

const Content = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  width: 100%;
  text-align: center;
  padding-bottom: 40px;
`

const Subtitle = styled.span`
  font-size: 1rem;
  font-weight: bold;
`

const ButtonContainer = styled.div<{ checked: boolean }>`
  width: 100%;
  margin: 5px 0;

  @media (min-width: ${breakpoints.md}) {
    width: 20%;
    margin: 5px 10px;
  }

  ${({ checked, theme }) =>
    checked &&
    `
    > .MuiFormControlLabel-root {
      background-color: ${theme.blue};
      color: white;
      box-shadow: 0 2px 10px 0 rgba(30, 30, 30, 0.4);
      border-color: ${theme.blue};

      & svg {
        fill: white
      }
    }
  `};
`

const Next = styled.div`
  margin: 90px auto 0;
  width: 100%;
  max-width: 700px;
  position: fixed;
  box-shadow: 0 -10px 27px 9px rgba(0, 0, 0, 0.06);
  bottom: 0;
  background: white;

  & a {
    margin: 10px 0;
    width: 202px;
    padding: 15px;
  }

  @media (min-width: ${breakpoints.sm}) {
    position: inherit;
    box-shadow: none;

    & a {
      width: 100%;
    }
  }
`

export default LikertScale
